/**
 * Client API per gestione Budget Attività
 * Gestisce persona + progetto + anno + tipo budget
 */

const API_BASE = '/api/v1/budget'

const budgetAPI = {
  /**
   * Ottieni tutti i budget con filtri opzionali
   * GET /api/v1/budget/
   */
  async getAll(filters = {}) {
    try {
      const params = new URLSearchParams()

      // Aggiungi filtri se presenti
      if (filters.id_people) params.append('id_people', filters.id_people)
      if (filters.id_project) params.append('id_project', filters.id_project)
      if (filters.year) params.append('year', filters.year)
      if (filters.tipo !== undefined) params.append('tipo', filters.tipo)

      const url = params.toString() ? `${API_BASE}/?${params}` : `${API_BASE}/`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('Errore getAll:', result)
        return {
          ok: false,
          message: result.message || `Errore HTTP ${response.status}`
        }
      }

      return result;
    } catch (error) {
      console.error('Errore di connessione getAll:', error)
      return {
        ok: false,
        message: 'Errore di connessione al server'
      }
    }
  },

  /**
   * Ottieni dettaglio singolo budget
   * GET /api/v1/budget/<id>
   */
  async getById(budgetId) {
    try {
      const response = await fetch(`${API_BASE}/${budgetId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('Errore getById:', result)
        return {
          ok: false,
          message: result.message || `Errore HTTP ${response.status}`
        }
      }

      return {
        ok: true,
        data: result.data || []
      }
    } catch (error) {
      console.error('Errore di connessione getById:', error)
      return {
        ok: false,
        message: 'Errore di connessione al server'
      }
    }
  },

  /**
   * Crea nuovo budget
   * POST /api/v1/budget/
   */
  async create(budgetData) {
    try {
      const response = await fetch(`${API_BASE}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(budgetData)
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('Errore create:', result)
        return {
          ok: false,
          message: result.message || `Errore HTTP ${response.status}`
        }
      }

      return {
        ok: true,
        message: result.message || 'Budget creato con successo',
        data: result.data
      }
    } catch (error) {
      console.error('Errore di connessione create:', error)
      return {
        ok: false,
        message: 'Errore di connessione al server'
      }
    }
  },

  /**
   * Aggiorna budget esistente
   * PUT /api/v1/budget/<id>
   */
  async update(budgetId, budgetData) {
    try {
      const response = await fetch(`${API_BASE}/${budgetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(budgetData)
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('Errore update:', result)
        return {
          ok: false,
          message: result.message || `Errore HTTP ${response.status}`
        }
      }

      return {
        ok: true,
        message: result.message || 'Budget aggiornato con successo',
        data: result.data
      }
    } catch (error) {
      console.error('Errore di connessione update:', error)
      return {
        ok: false,
        message: 'Errore di connessione al server'
      }
    }
  },

  /**
   * Elimina budget
   * DELETE /api/v1/budget/<id>
   */
  async delete(budgetId) {
    try {
      const response = await fetch(`${API_BASE}/${budgetId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('Errore delete:', result)
        return {
          ok: false,
          message: result.message || `Errore HTTP ${response.status}`
        }
      }

      return {
        ok: true,
        message: result.message || 'Budget eliminato con successo'
      }
    } catch (error) {
      console.error('Errore di connessione delete:', error)
      return {
        ok: false,
        message: 'Errore di connessione al server'
      }
    }
  },

  /**
   * Ottieni riepilogo budget per anno
   * GET /api/v1/budget/summary/<year>
   */
  async getSummary(year) {
    try {
      const response = await fetch(`${API_BASE}/summary/${year}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('Errore getSummary:', result)
        return {
          ok: false,
          message: result.message || `Errore HTTP ${response.status}`
        }
      }

      return {
        ok: true,
        data: result.data
      }
    } catch (error) {
      console.error('Errore di connessione getSummary:', error)
      return {
        ok: false,
        message: 'Errore di connessione al server'
      }
    }
  },

  /**
   * Utility: formatta valuta
   */
  formatCurrency(value) {
    if (!value) return '€ 0,00'
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(parseFloat(value))
  },

  /**
   * Utility: ottieni label per tipo budget
   */
  getTipoLabel(tipo) {
    const tipoMap = {
      0: 'Previsto',
      10: 'Consolidato',
      20: 'Fatturato'
    }
    return tipoMap[tipo] || 'Sconosciuto'
  },

  /**
   * Utility: ottieni classe CSS per tipo budget
   */
  getTipoClass(tipo) {
    const classMap = {
      0: 'badge bg-warning',
      10: 'badge bg-success',
      20: 'badge bg-primary'
    }
    return classMap[tipo] || 'badge bg-secondary'
  },

  /**
   * Utility: valida dati budget
   */
  validateBudget(budgetData) {
    const errors = []

    if (!budgetData.id_people) errors.push('Persona richiesta')
    if (!budgetData.id_project) errors.push('Progetto richiesto')
    if (!budgetData.year) errors.push('Anno richiesto')
    if (!budgetData.budget || budgetData.budget <= 0) errors.push('Budget deve essere maggiore di 0')
    if (budgetData.tipo === undefined || budgetData.tipo === null) errors.push('Tipo richiesto')

    return {
      valid: errors.length === 0,
      errors
    }
  }
}

export default budgetAPI